import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useApolloClient } from '@apollo/client';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { ProductReplacementPopupInfo } from './types';
import {
    MenuProductFragmentFragment,
    ProductState,
    useGetProductArrangementQuery,
    TotadminPermission,
} from 'data/__generated__';
import { useHasPermission } from 'hooks/useHasPermission';
import { CATALOG_PRODUCT_FRAGMENT } from 'data/fragments/product';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { DetailDisplayValue } from 'components/DetailsView/DetailDisplayValue';
import { TotemLabel } from 'components/TotemLabel';
import { BiArrowFromTop } from 'react-icons/bi';
import { ProductSelectForm } from 'components/Select/ProductSelectForm';
import { PermissionGate } from 'components/PermissionGate';

type FormData = {
    newProductId: string | null;
};

export type ProductArrangementsToUpdateType = {
    productArrangementId: string;
    productArrangementIndex: number;
    rotationNumber: number;
    currentProduct: MenuProductFragmentFragment['product'] | null;
    newProduct: MenuProductFragmentFragment['product'] | null;
    isInProductArrangement: boolean;
};

export function ProductReplacementPopup({
    isOpen,
    setIsOpen,
    productReplacementPopupInfo,
    updateProductArrangementsToUpdate,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    productReplacementPopupInfo: ProductReplacementPopupInfo | null;
    updateProductArrangementsToUpdate: ({
        productArrangementId,
        productArrangementIndex,
        rotationNumber,
        currentProduct,
        newProduct,
    }: ProductArrangementsToUpdateType) => void;
}) {
    const apolloClient = useApolloClient();

    const methods = useForm<FormData>({
        defaultValues: {
            newProductId: null,
        },
    });
    const {
        handleSubmit,
        formState: { isDirty },
        reset,
        setValue,
    } = methods;

    useEffect(() => {
        reset({ newProductId: null });
    }, [isOpen, reset]);

    // La gamme ne sert qu'aux suggestions : sans le droit, la requête ne part pas (son erreur, rendue telle
    // quelle, ferait tomber la page) et la recherche de produit reste possible.
    const canReadProductArrangements = useHasPermission(TotadminPermission.ProductArrangementsRead);
    const { data, loading, error } = useGetProductArrangementQuery({
        skip: !productReplacementPopupInfo?.productArrangementId || !canReadProductArrangements,
        variables: { productArrangementId: productReplacementPopupInfo?.productArrangementId || '' },
    });

    const aggregatedProducts = React.useMemo(() => {
        const productMap = new Map<
            string,
            { nbLocations: number; quantity: number; product: MenuProductFragmentFragment['product'] }
        >();

        if (!productReplacementPopupInfo) {
            return [];
        }

        productReplacementPopupInfo.products.forEach((product) => {
            const existingProduct = productMap.get(product.product._id);
            productMap.set(product.product._id, {
                ...existingProduct,
                product: {
                    _id: product.product._id,
                    fullname: product.product.fullname,
                    imageUrl: product.product.imageUrl,
                    state: product.product.state,
                    isMicrostore: product.product.isMicrostore,
                    isToBeArchived: product.product.isToBeArchived,
                    isWithoutStock: product.product.isWithoutStock,
                },
                nbLocations: Math.max(existingProduct?.nbLocations || 0, product.nbLocations),
                quantity: (existingProduct?.quantity || 0) + product.quantity,
            });
        });

        return Array.from(productMap.values());
    }, [productReplacementPopupInfo]);

    if (!productReplacementPopupInfo) {
        return null;
    }

    const { productArrangementId, productArrangementIndex, rotationNumber } = productReplacementPopupInfo;

    const productArrangementName = data?.productArrangementQuery?.name;
    const productArrangementProducts = data?.productArrangementQuery.productsWithStockMax || [];
    const productArrangementOptions = productArrangementProducts
        .filter(({ product }) => product?.state !== ProductState.Archived)
        .map(({ product }) => ({
            label: product.fullname,
            value: product._id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const removeProduct = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.preventDefault();
        setIsOpen(false);
        if (productArrangementId === null || productArrangementIndex === null || rotationNumber === null) {
            toast.error(
                `Pas d'emplacement sélectionné - impossible ${productArrangementId} ${productArrangementIndex} ${rotationNumber}`,
            );
            return;
        }
        updateProductArrangementsToUpdate({
            productArrangementId,
            productArrangementIndex,
            rotationNumber,
            currentProduct: productReplacementPopupInfo.products?.[0]?.product || null,
            newProduct: null,
            isInProductArrangement: true, // Removing product, so it's not relevant
        });
    };

    const selectProduct = (event: React.MouseEvent<HTMLElement, MouseEvent>, productId: string) => {
        event.preventDefault();
        setValue('newProductId', productId, { shouldDirty: true });
    };

    const onSubmit = handleSubmit((fields) => {
        const { newProductId } = fields;
        setIsOpen(false);
        if (productArrangementId === null || productArrangementIndex === null || rotationNumber === null) {
            toast.error(
                `Pas d'emplacement sélectionné - impossible ${productArrangementId} ${productArrangementIndex} ${rotationNumber}`,
            );
            return;
        }
        // new product might not be in product arrangement
        const productInArrangement = productArrangementProducts.find(({ product }) => product._id === newProductId);
        const newProduct =
            productInArrangement?.product ||
            apolloClient.readFragment({
                id: `Product:${newProductId}`,
                fragment: CATALOG_PRODUCT_FRAGMENT,
                fragmentName: 'CatalogProductFragment',
            });

        updateProductArrangementsToUpdate({
            productArrangementId,
            productArrangementIndex,
            rotationNumber,
            currentProduct: productReplacementPopupInfo.products?.[0]?.product || null,
            newProduct: newProduct || null,
            isInProductArrangement: !!productInArrangement,
        });
    });

    return (
        <TotemPopup
            title="Remplacer le produit dans cet emplacement"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            contentOverflow="visible"
        >
            <FormProvider {...methods}>
                <Form onSubmit={onSubmit}>
                    {/* @ts-expect-error to fix */}
                    {error ? <div>{error}</div> : null}
                    <ProductsList>
                        <TotemLabel>Produits actuels</TotemLabel>
                        {productReplacementPopupInfo.products.length === 0 ? (
                            <NoProducts>Aucun produit</NoProducts>
                        ) : (
                            aggregatedProducts.map((product, _index) => (
                                <ProductItem
                                    key={product.product._id}
                                    onClick={(e) => selectProduct(e, product.product._id)}
                                >
                                    <ProductName>
                                        <BiArrowFromTop size={13} />
                                        {product.product.fullname}
                                    </ProductName>
                                    <ProductQuantity>
                                        {product.nbLocations} emplacement(s) et {product.quantity} unité(s)
                                    </ProductQuantity>
                                </ProductItem>
                            ))
                        )}
                    </ProductsList>
                    <PermissionGate requiredPermissions={[TotadminPermission.ProductArrangementsRead]}>
                        <DetailDisplayValue label="Gamme de produit" value={productArrangementName || ''} />
                    </PermissionGate>
                    <ProductSelectForm
                        label="Nouveau produit"
                        name="newProductId"
                        placeholder="choisir un produit"
                        defaultOptions={productArrangementOptions}
                    />
                    <SubmitContainer>
                        <TotemPrimaryButton onClick={removeProduct} $isSecondaryStyle disabled={loading}>
                            Supprimer le produit
                        </TotemPrimaryButton>
                        <TotemPrimaryButton type="submit" disabled={!isDirty || loading}>
                            {productReplacementPopupInfo ? 'Confirmer' : 'Erreur - fermer'}
                        </TotemPrimaryButton>
                    </SubmitContainer>
                </Form>
            </FormProvider>
        </TotemPopup>
    );
}

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const SubmitContainer = styled.div`
    display: flex;
    gap: 4px;
    justify-content: space-around;
    margin-top: 15px;
    width: 100%;
`;

const ProductsList = styled.div`
    margin-bottom: 15px;
`;

const NoProducts = styled.div`
    color: #666;
    font-style: italic;
    margin-left: 10px;
`;

const ProductItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 10px;
    margin: 5px 0;
    background-color: #f5f5f5;
    border-radius: 4px;
    cursor: pointer;
    &:hover {
        background-color: #e5e5e5;
    }
`;

const ProductName = styled.span`
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ProductQuantity = styled.span`
    color: #666;
`;
